import { useEffect, useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import GlobalSearch from "./GlobalSearch";
import NotificationsBell from "./NotificationsBell";
import {
    LayoutGrid, KanbanSquare, Users, CheckSquare, Calculator,
    Sparkles, LogOut, Command, Settings as SettingsIcon, Globe, Trophy,
    Crown, CreditCard, Search, Shield, TrendingUp, Radar, Building2, Network, ScanSearch, Layers,
    ShieldAlert, Briefcase, Wallet, PieChart,
} from "lucide-react";

// ── Nav group definitions ──────────────────────────────────────────────────────

const DEAL_MGMT_NAV = [
    { to: "/",                      label: "Dashboard",        icon: LayoutGrid,  end: true },
    { to: "/pipeline",              label: "Pipeline",         icon: KanbanSquare },
    { to: "/contacts",              label: "Contacts",         icon: Users },
    { to: "/tasks",                 label: "Tasks",            icon: CheckSquare },
    { to: "/valuation",             label: "Valuation",        icon: Calculator },
    { to: "/forecast",              label: "Forecast",         icon: TrendingUp },
    { to: "/ai",                    label: "AI Hub",           icon: Sparkles },
    { to: "/outcomes",              label: "Outcomes",         icon: Trophy },
    { to: "/compliance",            label: "Compliance",       icon: Shield },
    { to: "/intelligence/leakage",  label: "Leakage",          icon: Radar },
];

const SOURCING_NAV = [
    { to: "/sourcing/mandates", label: "Mandates", icon: ScanSearch, end: true, testid: "nav-sourcing-mandates" },
    { to: "/sourcing/pipeline", label: "Pipeline",  icon: Layers,    end: true, testid: "nav-sourcing-pipeline" },
    { to: "/sourcing/sectors",  label: "Sector Packs", icon: PieChart, end: true, testid: "nav-sourcing-sectors" },
];

const DISTRESSED_NAV = [
    { to: "/distressed/watchlist", label: "Watchlist", icon: Radar, end: true, testid: "nav-distressed-watchlist" },
    { to: "/distressed/tupe-calculator", label: "TUPE Calculator", icon: ShieldAlert, end: true, testid: "nav-distressed-tupe-calculator" },
    { to: "/distressed/mandates", label: "Mandates", icon: Briefcase, end: true, testid: "nav-distressed-mandates" },
    { to: "/distressed/cashflow", label: "Cash Flow", icon: Wallet, testid: "nav-distressed-cashflow" },
];

const TRIAL_BANNER_STATUSES = new Set(["trialing", "past_due", "pending"]);

// ── Shared nav item component ─────────────────────────────────────────────────

function NavItem({ to, label, icon: Icon, end, testid }) {
    const tid = testid || `nav-${label.toLowerCase().replace(/\s+/g, '-')}`;
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={tid}
            className={({ isActive }) => isActive
                ? "flex items-center gap-3 py-[7px] pl-[10px] pr-3 rounded-[4px] text-[13px] font-medium text-white border-l-2 border-[#C9A227]"
                : "flex items-center gap-3 py-[7px] px-3 rounded-[4px] text-[13px] font-normal text-[#94A3B8] hover:text-white transition-colors"
            }
            style={({ isActive }) => isActive ? { background: 'rgba(255,255,255,0.08)' } : {}}
            onMouseEnter={e => { if (!e.currentTarget.classList.contains('text-white')) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={e => { if (!e.currentTarget.classList.contains('text-white')) e.currentTarget.style.background = ''; }}
        >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
            {label}
        </NavLink>
    );
}

// ── Section group header ──────────────────────────────────────────────────────

function SectionHeader({ children }) {
    return (
        <p
            className="px-3 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] select-none"
            style={{ color: '#C9A227' }}
        >
            {children}
        </p>
    );
}

export default function Layout() {
    const { user, logout, hasSurface } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const isSuperAdmin = user?.role === "super_admin";
    const isExternalViewer = user?.role === "external_viewer";
    const company = user?.company;
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        if (isSuperAdmin) return;
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setSearchOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isSuperAdmin]);

    return (
        <div className="min-h-screen flex bg-background">
            {/* ── Sidebar ── */}
            <aside
                className="w-56 flex flex-col sticky top-0 h-screen flex-shrink-0"
                style={{ background: '#0B1F3A', borderRight: '1px solid rgba(255,255,255,0.06)' }}
                data-testid="sidebar"
            >
                {/* Logo / tenant header */}
                <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
                        <div
                            className="w-7 h-7 flex items-center justify-center rounded-[4px]"
                            style={{ background: 'rgba(255,255,255,0.1)' }}
                        >
                            <Command className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div>
                            <div style={{ color: '#FFFFFF', fontSize: '15px', fontWeight: 600, lineHeight: 1 }}>Closvio</div>
                            <div style={{ color: '#64748B', fontSize: '11px', marginTop: '3px' }} className="truncate max-w-[120px]">
                                {isSuperAdmin ? "Super-Admin" : (company?.name || "M&A Platform")}
                            </div>
                        </div>
                    </Link>
                </div>

                {/* Nav */}
                <nav className="flex-1 px-3 pb-3 overflow-y-auto" data-testid="sidebar-nav">
                    {isSuperAdmin ? (
                        /* ── Super-admin: minimal nav ── */
                        <div className="pt-3 space-y-0.5">
                            <NavLink
                                to="/super-admin"
                                data-testid="nav-super-admin"
                                className={({ isActive }) => isActive
                                    ? "flex items-center gap-3 py-[7px] pl-[10px] pr-3 rounded-[4px] text-[13px] font-medium border-l-2 border-[#C9A227]"
                                    : "flex items-center gap-3 py-[7px] px-3 rounded-[4px] text-[13px] font-normal transition-colors"
                                }
                                style={({ isActive }) => isActive
                                    ? { color: '#C9A227', background: 'rgba(201,162,39,0.12)' }
                                    : { color: '#C9A227' }
                                }
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,162,39,0.08)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = e.currentTarget.classList.contains('border-l-2') ? 'rgba(201,162,39,0.12)' : ''; }}
                            >
                                <Crown className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                                Super-Admin
                            </NavLink>
                        </div>
                    ) : (
                        /* ── Grouped nav for normal users ── */
                        <>
                            {/* ── GROUP 1: CLOSVIO DEAL MANAGEMENT ─────── */}
                            {hasSurface("deal_management") && (
                                <>
                                    <SectionHeader>Closvio Deal Management</SectionHeader>
                                    <div className="space-y-0.5" data-testid="nav-group-deal-management">
                                        {DEAL_MGMT_NAV
                                            .filter(n => !(isExternalViewer && n.to === "/compliance"))
                                            .map(({ to, label, icon, end }) => (
                                                <NavItem key={to} to={to} label={label} icon={icon} end={end} />
                                            ))
                                        }
                                        {/* Admin-only items */}
                                        {isAdmin && (
                                            <NavItem
                                                to="/intelligence/origination"
                                                label="Origination"
                                                icon={Network}
                                            />
                                        )}
                                        {isAdmin && (
                                            <NavItem
                                                to="/managing-partner"
                                                label="Managing Partner"
                                                icon={Building2}
                                            />
                                        )}
                                    </div>
                                </>
                            )}

                            {/*
                                Opportunities is a public portal link, not a Deal Management
                                feature — it must NOT be nested inside any hasSurface(...) gate.
                                (This has regressed into the deal_management gate twice across
                                separate Layout.jsx changes; see
                                nav-opportunities-not-surface-gated.test.js for the regression
                                guard.)
                            */}
                            <a
                                href="/opportunities"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-3 py-[7px] px-3 rounded-[4px] text-[13px] font-normal text-[#94A3B8] hover:text-white transition-colors"
                                data-testid="nav-opportunities"
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                            >
                                <Globe className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                                Opportunities
                                <span
                                    className="ml-auto text-[10px] uppercase tracking-[0.08em]"
                                    style={{ color: '#475569' }}
                                >Public</span>
                            </a>

                            {/* ── GROUP 2: CLOSVIO SOURCING ─────────────── */}
                            {hasSurface("sourcing") && (
                                <div
                                    className="mt-3 pt-3"
                                    style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                                >
                                    <SectionHeader>Closvio Sourcing</SectionHeader>
                                    <div className="space-y-0.5" data-testid="nav-group-sourcing">
                                        {SOURCING_NAV.map(({ to, label, icon, end, testid }) => (
                                            <NavItem key={to} to={to} label={label} icon={icon} end={end} testid={testid} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ── GROUP 3: CLOSVIO DISTRESSED ───────────── */}
                            {hasSurface("distressed") && DISTRESSED_NAV.length > 0 && (
                                <div
                                    className="mt-3 pt-3"
                                    style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                                >
                                    <SectionHeader>Closvio Distressed</SectionHeader>
                                    <div className="space-y-0.5" data-testid="nav-group-distressed">
                                        {DISTRESSED_NAV.map(({ to, label, icon, end, testid }) => (
                                            <NavItem key={to} to={to} label={label} icon={icon} end={end} testid={testid} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ── ACCOUNT (Billing + Settings) ─────────── */}
                            <div
                                className="mt-3 pt-3 space-y-0.5"
                                style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                                data-testid="nav-group-account"
                            >
                                <NavLink
                                    to="/billing"
                                    data-testid="nav-billing"
                                    className={({ isActive }) => isActive
                                        ? "flex items-center gap-3 py-[7px] pl-[10px] pr-3 rounded-[4px] text-[13px] font-medium text-white border-l-2 border-[#C9A227]"
                                        : "flex items-center gap-3 py-[7px] px-3 rounded-[4px] text-[13px] font-normal text-[#94A3B8] hover:text-white transition-colors"
                                    }
                                    style={({ isActive }) => isActive ? { background: 'rgba(255,255,255,0.08)' } : {}}
                                    onMouseEnter={e => { if (!e.currentTarget.classList.contains('text-white')) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                    onMouseLeave={e => { if (!e.currentTarget.classList.contains('text-white')) e.currentTarget.style.background = ''; }}
                                >
                                    <CreditCard className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                                    Billing
                                    {company && TRIAL_BANNER_STATUSES.has(company.status) && (
                                        <span
                                            className="ml-auto text-[10px] uppercase tracking-[0.08em]"
                                            style={{ color: '#475569' }}
                                        >
                                            {company.status}
                                        </span>
                                    )}
                                </NavLink>
                                {isAdmin && (
                                    <NavLink
                                        to="/settings"
                                        data-testid="nav-settings"
                                        className={({ isActive }) => isActive
                                            ? "flex items-center gap-3 py-[7px] pl-[10px] pr-3 rounded-[4px] text-[13px] font-medium text-white border-l-2 border-[#C9A227]"
                                            : "flex items-center gap-3 py-[7px] px-3 rounded-[4px] text-[13px] font-normal text-[#94A3B8] hover:text-white transition-colors"
                                        }
                                        style={({ isActive }) => isActive ? { background: 'rgba(255,255,255,0.08)' } : {}}
                                        onMouseEnter={e => { if (!e.currentTarget.classList.contains('text-white')) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                        onMouseLeave={e => { if (!e.currentTarget.classList.contains('text-white')) e.currentTarget.style.background = ''; }}
                                    >
                                        <SettingsIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                                        Settings
                                    </NavLink>
                                )}
                            </div>
                        </>
                    )}
                </nav>

                {/* User profile / sign-out */}
                <div className="p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="px-3 py-2 mb-1">
                        <div className="text-[13px] font-medium truncate" style={{ color: '#CBD5E1' }}>
                            {user?.name}
                        </div>
                        <div className="text-[11px] truncate" style={{ color: '#64748B' }}>
                            {user?.email}
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        className="w-full flex items-center gap-2 px-3 py-[7px] rounded-[4px] text-[13px] font-normal transition-colors"
                        style={{ color: '#94A3B8' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#FFFFFF'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = '#94A3B8'; }}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.75} /> Sign out
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0 flex flex-col">
                {!isSuperAdmin && (
                    <div
                        className="sticky top-0 z-30 h-11 border-b border-[#E2E8F0] bg-white/95 backdrop-blur-sm flex items-center px-4 gap-2"
                        data-testid="topbar"
                    >
                        <button
                            type="button"
                            onClick={() => setSearchOpen(true)}
                            className="flex items-center gap-2 px-3 py-1.5 border border-[#E2E8F0] rounded-[6px] bg-[#F8FAFC] text-[13px] text-[#94A3B8] hover:border-[#CBD5E1] transition-colors w-72 max-w-full"
                            data-testid="open-global-search-btn"
                        >
                            <Search className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} />
                            <span className="flex-1 text-left">Search deals, contacts…</span>
                            <kbd className="text-[10px] font-mono px-1.5 py-0.5 border border-[#E2E8F0] rounded-[3px] bg-white hidden sm:inline">⌘K</kbd>
                        </button>
                        <div className="ml-auto flex items-center gap-2">
                            {company?.plan_type === "partner_comp" && (
                                <span
                                    data-testid="partner-badge"
                                    title="Complimentary top-tier access — Sadana Consulting M&A partnership"
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest cursor-default select-none"
                                    style={{ background: 'rgba(201,162,39,0.12)', color: '#C9A227', border: '1px solid rgba(201,162,39,0.35)' }}
                                >
                                    Partner
                                </span>
                            )}
                            <NotificationsBell />
                        </div>
                    </div>
                )}
                <div className="flex-1">
                    <Outlet />
                </div>
            </main>

            {!isSuperAdmin && (
                <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
            )}
        </div>
    );
}
